import { atom, useAtom, useSetAtom } from 'jotai';
/*
 * Filters map pins based on user-defined criteria.
 *
 * This state handles all of the map pins being rendered on the map view.
 * We store levels of clustering, and then individual pins for when the user zooms in.
 */

interface MapPin {
  id: number;
  slug: string;
  address: string;
  // [longitude, latitude]
  coordinates: number[];
}

// Generic cluster cell representation for an H3 index at any resolution
interface ClusterCell {
  // H3 index string (e.g., resolution h8/h9/h10)
  index: string;
  // Number of pins contained in this cell
  count: number;
  // Optional centroid in [lng, lat]
  centroid?: number[];
  // Optional list of pin ids contained in this cell
  pinIds?: number[];
}

type ClusteringState = Record<string, ClusterCell>;

// Clustering state at different resolutions
const clusteringH4Atom = atom<ClusteringState>({});
const clusteringH5Atom = atom<ClusteringState>({});
const clusteringH6Atom = atom<ClusteringState>({});
const clusteringH7Atom = atom<ClusteringState>({});
const clusteringH8Atom = atom<ClusteringState>({});
const clusteringH9Atom = atom<ClusteringState>({});
const clusteringH10Atom = atom<ClusteringState>({});
const clustersToDisplay = atom<ClusteringState>({});

export const useClustersToDisplay = () => useAtom(clustersToDisplay);

// Raw pins (used when fully unclustered / high zoom)
const mapPinsAtom = atom<MapPin[]>([]);

export const useClusteringH4 = () => {
  const [clusteringH4, updateClusteringH4] = useAtom(clusteringH4Atom);

  return {
    clusteringH4,
    updateClusteringH4,
  };
};

export const useClusteringH5 = () => {
  const [clusteringH5, updateClusteringH5] = useAtom(clusteringH5Atom);

  return {
    clusteringH5,
    updateClusteringH5,
  };
};

export const useClusteringH6 = () => {
  const [clusteringH6, updateClusteringH6] = useAtom(clusteringH6Atom);

  return {
    clusteringH6,
    updateClusteringH6,
  };
};

export const useClusteringH7 = () => {
  const [clusteringH7, updateClusteringH7] = useAtom(clusteringH7Atom);

  return {
    clusteringH7,
    updateClusteringH7,
  };
};

export const useClusteringH8 = () => {
  const [clusteringH8, updateClusteringH8] = useAtom(clusteringH8Atom);

  return {
    clusteringH8,
    updateClusteringH8,
  };
};

export const useClusteringH9 = () => {
  const [clusteringH9, updateClusteringH9] = useAtom(clusteringH9Atom);

  return {
    clusteringH9,
    updateClusteringH9,
  };
};

export const useClusteringH10 = () => {
  const [clusteringH10, updateClusteringH10] = useAtom(clusteringH10Atom);

  return {
    clusteringH10,
    updateClusteringH10,
  };
};

export const useClearAllState = () => {
  const setClusteringH4 = useSetAtom(clusteringH4Atom);
  const setClusteringH5 = useSetAtom(clusteringH5Atom);
  const setClusteringH6 = useSetAtom(clusteringH6Atom);
  const setClusteringH7 = useSetAtom(clusteringH7Atom);
  const setClusteringH8 = useSetAtom(clusteringH8Atom);
  const setClusteringH9 = useSetAtom(clusteringH9Atom);
  const setClusteringH10 = useSetAtom(clusteringH10Atom);
  const setMapPins = useSetAtom(mapPinsAtom);

  const clearAllMapPinsAndClusters = () => {
    setClusteringH4({});
    setClusteringH5({});
    setClusteringH6({});
    setClusteringH7({});
    setClusteringH8({});
    setClusteringH9({});
    setClusteringH10({});
    setMapPins([]);
  };

  return {
    clearAllMapPinsAndClusters,
  };
};
